// geoUtils.js
// GPS and geolocation utility functions for time tracking

// ============================================================================
// Constants
// ============================================================================

const EARTH_RADIUS_METERS = 6371000; // Earth's radius in meters
const METERS_PER_FOOT = 0.3048;
const FEET_PER_METER = 3.28084;

// ============================================================================
// Geolocation
// ============================================================================

/**
 * Get current GPS position as a Promise
 * @param {Object} options - Geolocation options
 * @param {boolean} options.enableHighAccuracy - Request high accuracy (default: true)
 * @param {number} options.timeout - Timeout in milliseconds (default: 10000)
 * @param {number} options.maximumAge - Max age of cached position in ms (default: 0)
 * @returns {Promise<Object>} { lat, lng, accuracy_m }
 * @throws {Error} GeolocationError with code and message
 */
export const getCurrentPosition = (options = {}) => {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
            reject(new GeolocationError('UNAVAILABLE', 'Geolocation is not supported by this browser'));
            return;
        }

        const defaultOptions = {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
        };

        const geoOptions = { ...defaultOptions, ...options };

        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                    accuracy_m: position.coords.accuracy
                });
            },
            (error) => {
                let errorType;
                let errorMessage;

                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorType = 'PERMISSION_DENIED';
                        errorMessage = 'Location permission was denied. Please enable location access in your browser settings.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorType = 'POSITION_UNAVAILABLE';
                        errorMessage = 'Location information is unavailable. Please check your GPS settings.';
                        break;
                    case error.TIMEOUT:
                        errorType = 'TIMEOUT';
                        errorMessage = 'Location request timed out. Please try again.';
                        break;
                    default:
                        errorType = 'UNKNOWN';
                        errorMessage = 'An unknown error occurred while getting location.';
                }

                reject(new GeolocationError(errorType, errorMessage));
            },
            geoOptions
        );
    });
};

/**
 * Custom error class for geolocation errors
 */
export class GeolocationError extends Error {
    constructor(code, message) {
        super(message);
        this.name = 'GeolocationError';
        this.code = code;
    }
}

// ============================================================================
// Distance Calculations
// ============================================================================

/**
 * Calculate distance between two points using Haversine formula
 * @param {number} lat1 - Latitude of point 1 (degrees)
 * @param {number} lng1 - Longitude of point 1 (degrees)
 * @param {number} lat2 - Latitude of point 2 (degrees)
 * @param {number} lng2 - Longitude of point 2 (degrees)
 * @returns {number} Distance in meters
 */
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
    // Convert degrees to radians
    const toRad = (deg) => deg * (Math.PI / 180);

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_METERS * c;
};

/**
 * Check if a position is within a geofence radius of a target location
 * @param {Object} position - Current position { lat, lng }
 * @param {Object} target - Target location { lat, lng }
 * @param {number} radiusMeters - Geofence radius in meters
 * @returns {boolean} True if within radius
 */
export const isWithinGeofence = (position, target, radiusMeters) => {
    const distance = calculateDistance(
        position.lat,
        position.lng,
        target.lat,
        target.lng
    );
    return distance <= radiusMeters;
};

// ============================================================================
// Unit Conversions
// ============================================================================

/**
 * Convert meters to feet
 * @param {number} meters - Distance in meters
 * @returns {number} Distance in feet
 */
export const metersToFeet = (meters) => {
    return meters * FEET_PER_METER;
};

/**
 * Convert feet to meters
 * @param {number} feet - Distance in feet
 * @returns {number} Distance in meters
 */
export const feetToMeters = (feet) => {
    return feet * METERS_PER_FOOT;
};

// ============================================================================
// Formatting
// ============================================================================

/**
 * Format distance for display (auto-selects appropriate unit)
 * @param {number} meters - Distance in meters
 * @param {boolean} useFeet - Use feet instead of meters (default: true for US)
 * @returns {string} Formatted distance string (e.g., "150 ft" or "45 m")
 */
export const formatDistance = (meters, useFeet = true) => {
    if (useFeet) {
        const feet = metersToFeet(meters);
        if (feet >= 5280) {
            const miles = feet / 5280;
            return `${miles.toFixed(1)} mi`;
        }
        return `${Math.round(feet)} ft`;
    } else {
        if (meters >= 1000) {
            const km = meters / 1000;
            return `${km.toFixed(1)} km`;
        }
        return `${Math.round(meters)} m`;
    }
};

// ============================================================================
// Default Export
// ============================================================================

const GeoUtils = {
    getCurrentPosition,
    GeolocationError,
    calculateDistance,
    isWithinGeofence,
    metersToFeet,
    feetToMeters,
    formatDistance
};

export default GeoUtils;
